import React, { useMemo, useRef, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  ShieldCheck,
  UserRound,
  Eye,
} from "lucide-react";
import StatusCellRenderer from "./StatusCellRenderer";
import DeleteConfirmationPopup from "./DeleteConfirmation";
import AddEditUserModal from "./AddEditUserModal";
import { AllCommunityModule, ModuleRegistry, themeQuartz, colorSchemeLight } from "ag-grid-community";

const userGridTheme = themeQuartz.withPart(colorSchemeLight).withParams({
  backgroundColor: "#ffffff",
  foregroundColor: "#1f2937",
  accentColor: "#3b82f6",
  headerTextColor: "#ffffff",
  headerBackgroundColor: "#1a325d",
  oddRowBackgroundColor: "#f9fafb",
  chromeBackgroundColor: "#f9fafb",
});

ModuleRegistry.registerModules([AllCommunityModule]);

const UserTable = ({
  users,
  onDelete,
  onUserAdded,
}) => {

  const [deletePopup, setDeletePopup] = useState({ isOpen: false, user: null });
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const gridRef = useRef();

  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalMode("edit");
    setAddUserModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    setDeletePopup({ isOpen: true, user });
  };

  const handleDeleteConfirm = () => {
    if (deletePopup.user) {
      onDelete?.(deletePopup.user._id);
      setDeletePopup({ isOpen: false, user: null });
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "fullName",
        valueGetter: (params) =>
          `${params.data.firstName || ""} ${params.data.lastName || ""}`,
        cellRenderer: (params) => (
          <span className="text-sm font-medium">{params.value}</span>
        ),
      },
      {
        headerName: "Username",
        field: "username",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Email",
        field: "email",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Role",
        field: "role",
        cellRenderer: (params) => {
          const role = params.value?.toLowerCase();

          let icon = null;
          let bgClass = "";
          let textColor = "";
          let label = "";

          switch (role) {
            case "admin":
              icon = <ShieldCheck size={14} className="mr-1 text-blue-700" />;
              bgClass = "bg-blue-100";
              textColor = "text-blue-800";
              label = "Admin";
              break;

            case "user":
              icon = <UserRound size={14} className="mr-1 text-emerald-700" />;
              bgClass = "bg-emerald-100";
              textColor = "text-emerald-800";
              label = "User";
              break;

            case "readonly":
              icon = <Eye size={14} className="mr-1 text-gray-700" />;
              bgClass = "bg-gray-100";
              textColor = "text-gray-800";
              label = "ReadOnly";
              break;

            default:
              icon = null;
              bgClass = "bg-gray-200";
              textColor = "text-gray-800";
              label = role;
          }

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm ${bgClass} ${textColor}`}
            >
              {icon}
              {label}
            </span>
          );
        },
      },
      {
        headerName: "Company",
        field: "company",
        width: 170,
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value.name}
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "isActive",
        width: 120,
        cellRenderer: StatusCellRenderer,
        valueFormatter: (params) => (params.value ? "Active" : "Inactive"),
        filterParams: {
          valueFormatter: (value) => (value === true ? "Active" : "Inactive"),
          values: [true, false],
        },
      },
      {
        headerName: "Actions",
        width: 120,
        suppressMenu: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
        cellRenderer: (params) => (
          <div className="flex items-center gap-2 h-full">
            <button
              onClick={() => handleEdit(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-blue-100 transition-all duration-200 border border-blue-200 hover:shadow-blue-300"
            >
              <Edit className="w-4 h-4 text-blue-600 group-hover:scale-110 group-hover:text-blue-700 transition-all" />
            </button>

            <button
              onClick={() => handleDeleteClick(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-rose-100 transition-all duration-200 border border-rose-200 hover:shadow-rose-300"
            >
              <Trash2 className="w-4 h-4 text-rose-500 group-hover:scale-110 group-hover:text-rose-700 transition-all" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{}}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {/* Title with Icon */}
        <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
          <Users className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
          User Management
        </h2>

        {/* Add User Button */}
        <button
          onClick={() => setAddUserModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-tr from-[#1a325d] to-[#27ade3] shadow-md hover:scale-105 transition-transform rounded-lg"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add User
        </button>
      </div>

      {/* Table Container */}
      <Box
        className="ag-theme-quartz relative"
        sx={
          {
            // height: 620,
            // borderRadius: 12,
            // overflow: 'hidden',
            // backgroundColor: '#ffffff',
            // border: '1px solid #e5e7eb',
            // boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          }
        }
      >
        <AgGridReact
          ref={gridRef}
          theme={userGridTheme}
          rowData={users}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            flex: 1,
          }}
          domLayout="autoHeight"
          rowHeight={45}
          onGridReady={() => {
            setTimeout(() => {
              const header = document.querySelector(".ag-header");
              if (header) {
                header.style.background =
                  "linear-gradient(to top right, #1a325d, #27ade3)";
                header.style.color = "#ffffff";
              }
            }, 0); // short delay to ensure DOM is mounted
          }}
        />
      </Box>

      {/* Modals */}
      <DeleteConfirmationPopup
        isOpen={deletePopup.isOpen}
        onClose={() => setDeletePopup({ isOpen: false, user: null })}
        onConfirm={handleDeleteConfirm}
        userName={
          deletePopup.user
            ? `${deletePopup.user.firstName} ${deletePopup.user.lastName}`
            : ""
        }
      />

      {addUserModalOpen && (
        <AddEditUserModal
          open={addUserModalOpen}
          onClose={() => {
            setAddUserModalOpen(false);
            setSelectedUser(null);
            setModalMode("add");
          }}
          onSubmit={() => {
            setAddUserModalOpen(false);
            setSelectedUser(null);
            setModalMode("add");
            onUserAdded?.();
          }}
          mode={modalMode}
          user={selectedUser}
        />
      )}
    </Box>
  );
};

export default UserTable;
